import { RichTextBlock } from 'prismic-reactjs'
import styled from 'styled-components'
import Image from 'next/image'
import { ImageField, RichTextField } from '@prismicio/client'

import RichText from '../RichText'
import CardContainer from '../CardContainer/CardContainer'
import { BackgroundColorKey, backgroundColors } from '../../styles/backgroundColors'

interface IRichTextAndImageCard {
	content: RichTextField
	background_color?: BackgroundColorKey | null
	display_image: boolean
	image: ImageField
}

const RichTextAndImageCard: React.FC<IRichTextAndImageCard> = ({ content, background_color, display_image, image }) => {
	const backgroundColor = backgroundColors(background_color ?? 'Blue (100)')

	return (
		<StyledCardContainer
			options={{
				backgroundColor: backgroundColor,
			}}
			withImage={display_image}
		>
			<SpacingContainer>
				{display_image && (
					<RichImage
						src={image.url ?? ''}
						alt=''
						quality={100}
						width={741}
						height={460}
					/>
				)}
				<StyledRichText content={content} />
			</SpacingContainer>
		</StyledCardContainer>
	)
}

export default RichTextAndImageCard

const StyledCardContainer = styled(CardContainer)<{ withImage: boolean }>`
	padding-top: ${(props) => (props.withImage ? '3rem' : '6rem')};
	padding-bottom: var(--spacer-base);

	a,
	a > strong {
		color: ${(p) => (p.options.backgroundColor === 'var(--orange)' ? 'var(--off-white)' : 'var(--orange)')};
	}
`

const RichImage = styled(Image)`
	grid-column-start: 2;
	max-width: 65ch;
	width: 100%;
	max-height: 460px;
	object-fit: cover;
	border-radius: 56px;
	margin-bottom: 40px;
`

const SpacingContainer = styled.div`
	grid-column: 2/-2;
	display: grid;
	grid-template-columns: 1fr auto 1.6fr;
`

const StyledRichText = styled(RichText)`
	grid-column-start: 2;
	* {
		max-width: 65ch;
		color: var(--black);
	}
	a {
		text-decoration: underline;
	}
	h1 {
		font-weight: 500;
		font-size: 3.5rem;
		line-height: 4rem;

		margin-top: 3.5rem;
		margin-bottom: 1.5rem;

		color: var(--deep-blue);
	}

	h2 {
		font-weight: 500;
		font-size: 2.5rem;
		line-height: 3.5rem;

		margin-top: 3.5rem;
		margin-bottom: 1.5rem;

		color: var(--deep-blue);
	}

	h3 {
		font-weight: 400;
		font-size: 2rem;
		line-height: 2.5rem;

		margin-top: 3.5rem;
		margin-bottom: 1rem;

		color: var(--deep-blue);
	}

	h4 {
		font-weight: 500;
		font-size: 1.5rem;
		line-height: 1.5rem;

		margin-top: 2rem;
		margin-bottom: 0.5rem;

		color: var(--deep-blue);
	}

	h5 {
		font-weight: 500;
		font-size: 1.375rem;
		line-height: 1.5rem;

		color: var(--deep-blue);

		margin-top: 2rem;
		margin-bottom: 0.5rem;
	}

	h6 {
		font-weight: 500;
		font-size: 1.25rem;
		line-height: 1.5rem;

		color: var(--deep-blue);

		margin-top: 1rem;
		margin-bottom: 0.5rem;
	}

	p {
		font-weight: 300;
		font-size: 1.25rem;
		line-height: 2rem;

		color: var(--black);

		margin: 0;
		margin-bottom: var(--spacer-xs);

		font-style: normal;
	}

	li {
		margin-bottom: var(--spacer-2xs);
	}
`
